import {onBeforeUnmount, onMounted, ref} from 'vue';

/**
 * Takes the files dropped anywhere on the page
 */
export function usePageFileDrop(onFiles: (files: File[]) => void, isEnabled: () => boolean) {
    const isDragging = ref(false);

    function carriesFiles(event: DragEvent): boolean {
        return event.dataTransfer?.types.includes('Files') === true && isEnabled();
    }

    function onDragEnter(event: DragEvent): void {
        if (! carriesFiles(event)) {
            return;
        }

        isDragging.value = true;
    }

    function onDragLeave(event: DragEvent): void {
        // the files left the window, not just one element for another
        if (event.relatedTarget === null) {
            isDragging.value = false;
        }
    }

    function onDragOver(event: DragEvent): void {
        if (carriesFiles(event)) {
            event.preventDefault();
        }
    }

    function onDrop(event: DragEvent): void {
        if (! carriesFiles(event)) {
            return;
        }

        event.preventDefault();
        onFiles([...(event.dataTransfer?.files ?? [])]);
    }

    function onDropAnywhere(): void {
        isDragging.value = false;
    }

    onMounted(() => {
        // in the capture phase, so the overlay hides even over an element that stops them, such as the file picker
        window.addEventListener('drop', onDropAnywhere, true);
        window.addEventListener('dragleave', onDragLeave, true);
        window.addEventListener('dragenter', onDragEnter);
        window.addEventListener('dragover', onDragOver);
        window.addEventListener('drop', onDrop);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('drop', onDropAnywhere, true);
        window.removeEventListener('dragleave', onDragLeave, true);
        window.removeEventListener('dragenter', onDragEnter);
        window.removeEventListener('dragover', onDragOver);
        window.removeEventListener('drop', onDrop);
    });

    return {isDragging};
}
